import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";

import useTokenAccess from "src/hooks/auth/useTokenAccess";

import { apiGatewayUrl } from "src/utils/services/servicesHelper";
import { isProduction } from "src/utils/env/envHelper";

import {
  EMR_LIVE_EVENT_KINDS,
  EMRLiveEventPayload,
  invalidateAllForClinic,
  invalidateForEvent,
} from "./emrLiveEvents";
import { setEMRLive } from "./emrLiveStatus";

export const EMR_LIVE_RESTART_MS = 30_000;
const MAX_RECONNECT_DELAY_MS = 60_000;

export const emrLiveHubUrl = (clinicId: string) =>
  `${apiGatewayUrl}/emr/hubs/portal?clinicId=${encodeURIComponent(clinicId)}`;

const reconnectDelay = (previousRetryCount: number) =>
  Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** previousRetryCount);

const useEMRLiveUpdates = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (!clinicId) return;

    const connection = new HubConnectionBuilder()
      .configureLogging(isProduction ? LogLevel.None : LogLevel.Warning)
      .withUrl(emrLiveHubUrl(clinicId), {
        accessTokenFactory: () => getTokenRef.current(),
        headers: { "X-Clinic-Id": clinicId },
        withCredentials: false,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
          reconnectDelay(previousRetryCount),
      })
      .build();

    let isStopped = false;
    let restartTimer: ReturnType<typeof setTimeout> | undefined;

    EMR_LIVE_EVENT_KINDS.forEach((kind) =>
      connection.on(kind, (payload: EMRLiveEventPayload) => {
        invalidateForEvent(queryClient, clinicId, kind, payload);
      })
    );

    const start = () =>
      connection
        .start()
        .then(() => {
          if (isStopped) return;
          setEMRLive(true);
          invalidateAllForClinic(queryClient, clinicId);
        })
        .catch(() => {
          if (isStopped) return;
          setEMRLive(false);
          restartTimer = setTimeout(start, EMR_LIVE_RESTART_MS);
        });

    connection.onreconnecting(() => setEMRLive(false));
    connection.onreconnected(() => {
      setEMRLive(true);
      invalidateAllForClinic(queryClient, clinicId);
    });
    connection.onclose(() => {
      setEMRLive(false);
      if (isStopped) return;
      restartTimer = setTimeout(start, EMR_LIVE_RESTART_MS);
    });

    start();

    return () => {
      isStopped = true;
      clearTimeout(restartTimer);
      setEMRLive(false);
      connection.stop();
    };
  }, [clinicId, queryClient]);
};

export default useEMRLiveUpdates;
